import React, { useEffect, useMemo, useState } from 'react'
import "./prescription-print.scss"
import { calculateDaysBetween, formatAgeYearMonth, dateFormat, formatDate, provisionalDxTitle, medicationLineText } from '../../../utils/common-funtions/common'
import rxImage from "../../../assets/images/RxIcon.png"
import { useSelector } from 'react-redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faDotCircle, faEye } from '@fortawesome/free-solid-svg-icons'
import { detailsFileDisplayUrl } from '../../profile-info/detailsFiles'

function isDetailsSignatureSlot(value) {
    return (
        value &&
        typeof value === 'object' &&
        typeof value.key === 'string' &&
        value.key.trim().length > 0
    )
}

const PrescriptionBody = ({
    fromIOS = false,
    info,
    patientDetails,
    drDetails,
    publicPrescriptionSetting = { sig: false, patientSection: false },
    signature,
    /** When true, never use logged-in user legacy `details.signature` (e.g. staff printing another doctor’s Rx). */
    signatureControlled = false,
    medicineAndAdviceOnly=false
}) => {
    const [signUrl, setSignUrl] = useState("")
    const [backGround, setBackGround] = useState("")
    const [reasonForVisit, setReasonForVisit] = useState(info?.reasonForVisit?.trim().split('\n') || [])
    const [historyOfPresetIllness, setHistoryOfPresetIllness] = useState(info?.historyOfPresetIllness?.trim().split('\n') || [])
    const [currentMedication, setCurrentMedication] = useState(info?.currentMedication?.trim().split('\n') || [])
    const [extraNote, setExtraNote] = useState(info?.extraNote?.trim().split('\n') || [])
    // Carried on the patient, like pastMedications -- not on the visit.
    const currentMedications = patientDetails?.currentMedications || []
    const [provisionalDx, setProvisionalDx] = useState(info?.provisionalDx?.trim().split('\n') || [])
    const [differentialDx, setDifferentialDx] = useState(info?.differentialDx?.trim().split('\n') || [])
    const [defaultFontSize, setDefaultFontSize] = useState("12pt")
    const [medicineFontSize, setMedicineFontSize] = useState("14pt")
    const [prescriptionAdviceFontSize, setPrescriptionAdviceFontSize] = useState("13pt")
    const [customPatientDetails, setCustomPatientDetails] = useState([])
    const [medicineDesign, setMedicineDesign] = useState(1)
    const { user } = useSelector((state) => state.auth);
    const [referral, setReferral] = useState()
    const [discountNote, setDiscountNote] = useState(drDetails?.config?.discountNote?.trim().split('\n') || [])
    const [signatureNote, setSignatureNote] = useState(drDetails?.config?.signatureNote?.trim().split('\n') || [])
    const [nextVisitInstructions, setNextVisitInstructions] = useState(info?.followUpReason?.trim()?.split('\n') || [])
    const iconSize = 11

    
    const customHeaderArrange = (data) => {
        const result = data.reduce((acc, item) => {
            if (item.visible) {
                acc[item.value] = item;
            }
            return acc;
        }, {});
        setCustomPatientDetails(result)
    }

    useMemo(() => {
        if (drDetails?.config?.prescriptionNamePosition == 3) {
            customHeaderArrange(drDetails?.config?.customizeHeader)
        }
        if (drDetails?.config?.prescriptionDefaultFontSize)
            setDefaultFontSize(drDetails?.config?.prescriptionDefaultFontSize + "pt")
        if (drDetails?.config?.medicineFontSize)
            setMedicineFontSize(drDetails?.config?.medicineFontSize + "pt")
        if (drDetails?.config?.prescriptionAdviceFontSize)
            setPrescriptionAdviceFontSize(drDetails?.config?.prescriptionAdviceFontSize + "pt")
        if (drDetails?.config?.discountNote)
            setDiscountNote(drDetails?.config?.discountNote.trim().split('\n') || [])
        if (drDetails?.config?.signatureNote)
            setSignatureNote(drDetails?.config?.signatureNote.trim().split('\n') || [])
    }, [drDetails?.config])


    function boldParenthesis(text) {
        const parts = text.split(/(\(.*?\))/); // keep parentheses text

        return parts.map((part, index) => {
            const isParenthesis = part.startsWith("(") && part.endsWith(")");

            // check for + or - inside parentheses
            const hasOperator = /[+-]/.test(part);

            if (isParenthesis && hasOperator) {
                return <strong key={index}>{part}</strong>;
            }

            return part;
        });
    }

    useMemo(() => {
        if (info) {
            setReasonForVisit(info?.reasonForVisit?.trim()?.split('\n') || [])
            setHistoryOfPresetIllness(info?.historyOfPresetIllness?.trim()?.split('\n') || [])
            setExtraNote(info?.extraNote?.trim()?.split('\n') || [])
            setProvisionalDx(info?.provisionalDx?.trim()?.split('\n') || [])
            setDifferentialDx(info?.differentialDx?.trim()?.split('\n') || [])
            setCurrentMedication(info?.currentMedication?.trim()?.split('\n') || [])
            setNextVisitInstructions(info?.followUpReason?.trim()?.split('\n') || [])
        }
    }, [info])

    const viewBackGroundImage = async (url) => {
        let imageUrl = await getImageAsFile(process.env.REACT_APP_FILE_BASE_URL + '/' + encodeURIComponent(url))
        setBackGround(imageUrl)
    }




    async function getImageAsFile(imageUrl) {
        try {
            let token = JSON.parse(localStorage.getItem("user"))?.token;
            const response = await fetch(imageUrl, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });
            const buffer = await response.arrayBuffer();
            const type = response.headers.get('Content-Type');
            const blob = new Blob([buffer], { type });
            return URL.createObjectURL(blob)
        } catch (error) {
            console.error('Error:', error);
            return null;
        }
    }

    useMemo(() => {
        if (drDetails?.config?.prescriptionBackgroundImage) {
            viewBackGroundImage(drDetails?.details?.backgroundImage)
        }
        if (drDetails?.config?.medicineSectionDesign)
            setMedicineDesign(drDetails?.config?.medicineSectionDesign)
    }, [drDetails?.config?.signatureWithImage, drDetails?.config?.prescriptionBackgroundImage, drDetails?.config?.medicineSectionDesign])

    useMemo(() => {
        if (info?.referral) {
            setReferral(info?.referral?.trim()?.split('\n'))
        }
    }, [info])

    useEffect(() => {
        let cancelled = false

        async function resolveSignatureUrl() {
            const slotFromProp = isDetailsSignatureSlot(signature) ? signature : null
            const slotFromDoctor =
                publicPrescriptionSetting?.sig && isDetailsSignatureSlot(drDetails?.details?.files?.signature)
                    ? drDetails.details.files.signature
                    : null
            const slot = slotFromProp || slotFromDoctor
            // if (slot) {
            //     const url = await detailsFileDisplayUrl(slot, { concatPrivate: true })
            //     if (!cancelled) setSignUrl(url || '')
            //     return
            // }

            if (slot?.key){
                setSignUrl( process.env.REACT_APP_PRIVATE_S3_URL + '/' + slot?.key)
            }

            

            // if (typeof signature === 'string' && signature.trim()) {
            //     const imageUrl = await getImageAsFile(
            //         process.env.REACT_APP_FILE_BASE_URL + '/' + encodeURIComponent(signature)
            //     )
            //     if (!cancelled) setSignUrl(imageUrl || '')
            //     return
            // }

            // const legacyPath = publicPrescriptionSetting?.sig
            //     ? (drDetails?.details?.signature || '')
            //     : (signatureControlled ? '' : (user?.user?.details?.signature || ''))

            // if (legacyPath && typeof legacyPath === 'string') {
            //     const imageUrl = await getImageAsFile(
            //         process.env.REACT_APP_FILE_BASE_URL + '/' + encodeURIComponent(legacyPath)
            //     )
            //     if (!cancelled) setSignUrl(imageUrl || '')
            //     return
            // }

            // if (!cancelled) setSignUrl('')
        }

        resolveSignatureUrl()
        return () => {
            cancelled = true
        }
    }, [
        signature,
        signatureControlled,
        publicPrescriptionSetting?.sig,
        drDetails?.details?.signature,
        drDetails?.details?.files?.signature,
    ])

    return (
        <div style={{ position: "relative", fontSize: defaultFontSize }} className={"prescription-body mb-1"}>
            {
                (drDetails?.config?.prescriptionNamePosition == 1 || publicPrescriptionSetting?.patientSection) &&
                <div className='justify-content-between patientInfo pb-0 pt-0 px-4 mb-0'
                    style={{display:"flex", visibility: medicineAndAdviceOnly ? "hidden" : "visible", fontSize: defaultFontSize, borderBottom: "1px solid black", borderTop: "1px solid black" }}>
                    <p>Name: <span> {info?.patientName || patientDetails?.name}</span></p>
                    <p>Id: <span> {info?.patientId || patientDetails?.patientId || " "}</span></p>
                    <p>Age: <span> {formatAgeYearMonth(info?.patientDob || patientDetails?.dob) || " "}</span></p>
                    <p>Gender: <span> {info?.patientGender || patientDetails?.gender || " "}</span></p>
                    <p>Date: <span> {formatDate(info?.appointmentDate)}</span></p>
                </div>
            }
            {drDetails?.config?.prescriptionNamePosition == "3" && !publicPrescriptionSetting?.patientSection && Object.keys(customPatientDetails).length > 0 && <div className='d-flex flex-wrap' style={{ visibility: medicineAndAdviceOnly ? "hidden" : "visible" }}>
                {
                    Object.entries(customPatientDetails).map(([key, item]) => {
                        if (item?.value == "age")
                            return (
                                <div key={key} style={{ width: item.width + "%", position: "relative" }} className=' py-1'>
                                    <p style={{ position: "absolute", left: item.position_left + "%", top: "0%" }} className='my-0'>{formatAgeYearMonth(info?.patientDob || patientDetails?.dob)}</p>
                                </div>
                            )
                        else if (item?.value == "patientId")
                            return (
                                <div key={key} style={{ width: item.width + "%", position: "relative" }} className=' py-3'>
                                    <p style={{ position: "absolute", left: item.position_left + "%", top: "0%" }} className='my-0'>ID : {info?.patientId || patientDetails?.patientId}</p>
                                </div>
                            )
                        else if (item?.value == "dob")
                            return (
                                <div key={key} style={{ width: item.width + "%", position: "relative" }} className=' py-3'>
                                    <p style={{ position: "absolute", left: item.position_left + "%", top: "0%" }} className='my-0'>{formatDate(info?.patientDob || patientDetails?.dob)}</p>
                                </div>
                            )
                        else if (item?.value == "appointmentDate")
                            return (
                                <div key={key} style={{ width: item.width + "%", position: "relative" }} className=' py-3'>
                                    <p style={{ position: "absolute", left: item.position_left + "%", top: "0%" }} className='my-0'>{formatDate(info?.appointmentDate)}</p>
                                </div>
                            )
                        else if (item?.value == "name")
                            return (
                                <div key={key} style={{ width: item.width + "%", position: "relative" }} className=' py-3'>
                                    <p style={{ position: "absolute", left: item.position_left + "%", top: "0%" }} className='my-0'>{info?.patientName || patientDetails?.name}</p>
                                </div>
                            )
                        else if (item?.value == "gender")
                            return (
                                <div key={key} style={{ width: item.width + "%", position: "relative" }} className=' py-3'>
                                    <p style={{ position: "absolute", left: item.position_left + "%", top: "0%" }} className='my-0'>{info?.patientGender || patientDetails?.gender}</p>
                                </div>
                            )
                    })
                }
            </div>}
            <div className='d-flex ' style={{
                overflowY: "scroll",
                height: `${fromIOS && "900px"}`,
                minHeight: fromIOS ? "900px" : drDetails?.config?.prescriptionBodyHeight ? drDetails?.config?.prescriptionBodyHeight + "px" : '1000px',
            }}>

                <div className={``} style={{
                    zIndex: "3333",
                    width: drDetails?.config?.prescriptionBodyLeftSectionWidth ? drDetails?.config?.prescriptionBodyLeftSectionWidth + "%" : "34%",
                    borderRight: (drDetails?.config?.prescriptionDivider && !medicineAndAdviceOnly )? "1px solid black" : "none",
                    paddingRight: "20px",
                    paddingLeft: drDetails?.config?.prescriptionBodyLeftPadding ? (drDetails?.config?.prescriptionBodyLeftPadding + "px") : "20px",
                    paddingTop: "40px",
                    // minHeight: (drDetails?.config?.prescriptionBodyHeight - 100) + "px"
                }}>
                    {
                        drDetails?.config?.prescriptionNamePosition == 2 && !publicPrescriptionSetting?.patientSection &&
                        <div className='patientInfo mb-4 mr-2' style={{ display:medicineAndAdviceOnly?"none":"",fontSize: defaultFontSize }}>
                            <table id="example2"
                                className="table table-hover table-bordered table-sm table-responsive{-sm|-md|-lg|-xl}">
                                <tbody>
                                    <tr>
                                        <td className={"col-4"}>Name</td>
                                        <td className={"col-8"}><span> {info?.patientName || patientDetails?.name}</span></td>
                                    </tr>
                                    <tr>
                                        <td className={"col-4"}>Id</td>
                                        <td className={"col-8"}><span> {info?.patientId || patientDetails?.patientId || " "}</span></td>
                                    </tr>
                                    <tr>
                                        <td className={"col-4"}>Age</td>
                                        <td className={"col-8"}>
                                            <span> {formatAgeYearMonth(info?.patientDob || patientDetails?.dob) || " "}</span>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className={"col-4"}>Gender</td>
                                        <td className={"col-8"}><span> {info?.patientGender || patientDetails?.gender || " "}</span></td>
                                    </tr>
                                    <tr>
                                        <td className={"col-4"}>Date</td>
                                        <td className={"col-8"}>
                                            <span> {formatDate(info?.appointmentDate)}</span></td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    }
                    <div style={{display:medicineAndAdviceOnly?"none":""}}>
                        {
                            <div className='mb-2' style={{ fontSize: defaultFontSize }}>

                                {/* {
                                    ((reasonForVisit?.length > 0 && reasonForVisit[0]?.length > 0) || (historyOfPresetIllness?.length > 0 && historyOfPresetIllness[0]?.length > 0)) &&
                                    <span className="title my-0 mb-1">CHIEF COMPLAINTS</span>
                                }  */}
                                {
                                    reasonForVisit?.length > 0 && reasonForVisit[0]?.length > 0 && <div className="px-1 mb-2">
                                        {/* <p style={{ fontWeight: "700" }} className='pb-0 mb-0 text-dark'>Reason for visit</p> */}
                                        <p style={{ fontWeight: "700" }} className='pb-0 mb-0 text-dark'>Chief Complaint</p>
                                        {
                                            reasonForVisit?.map((reason, index) => {
                                                if (reason != "")
                                                    return (<p className='my-0 ml-1' key={index}> <FontAwesomeIcon
                                                        icon={faDotCircle}
                                                        width={iconSize}
                                                        className="text-secondary"
                                                    /> {reason}</p>)
                                            })
                                        }
                                    </div>
                                }
                                {
                                    historyOfPresetIllness?.length > 0 && historyOfPresetIllness[0]?.length > 0 &&
                                    <div className='mb-1 px-1'>
                                        <p style={{ fontWeight: "700" }} className='pb-0 mb-0 text-dark'>History of Present
                                            illness</p>
                                        {
                                            historyOfPresetIllness?.map((history, index) => {
                                                if (history != "")
                                                    return (<p className='my-0 ml-1' key={index}><FontAwesomeIcon
                                                        icon={faDotCircle}
                                                        width={iconSize}
                                                        className="text-secondary"
                                                    /> {history}</p>)
                                            })
                                        }
                                    </div>
                                }
                                {
                                    info?.extraNote &&
                                    <div className='mb-1 px-1'>
                                        <p style={{ fontWeight: "700" }} className='pb-0 mb-0 text-dark'>Note</p>
                                        {
                                            extraNote?.map((history, index) => {
                                                if (history != "")
                                                    return (<p className='my-0 ml-1' key={index}><FontAwesomeIcon
                                                        icon={faDotCircle}
                                                        width={iconSize}
                                                        className="text-secondary"
                                                    /> {history}</p>)
                                            })
                                        }
                                    </div>
                                }


                                {
                                    /* Legacy free text and the structured list share one heading:
                                       a visit mid-transition can legitimately hold both. */
                                    ((currentMedication?.length > 0 && currentMedication[0]?.length > 0) || currentMedications?.length > 0) &&
                                    <div className='mb-1 '>
                                        <span className="title my-0 mb-1 px-1">Current Medication</span>
                                        {
                                            currentMedication?.map((medicine, index) => {
                                                if (!medicine?.length) return null
                                                return (<p className='my-0 px-2' key={index}><FontAwesomeIcon
                                                    icon={faDotCircle}
                                                    width={iconSize}
                                                    className="text-secondary"
                                                />  {medicine}</p>)
                                            })
                                        }
                                        {
                                            currentMedications?.map((medicine, index) => {
                                                return (<p className='my-0 px-2' key={`cm-${index}`}><FontAwesomeIcon
                                                    icon={faDotCircle}
                                                    width={iconSize}
                                                    className="text-secondary"
                                                />  {medicationLineText(medicine)}</p>)
                                            })
                                        }
                                    </div>
                                }
                            </div>

                        }
                    </div>



                    <div style={{display:medicineAndAdviceOnly?"none":''}}>
                        {
                            info?.vitals &&
                            <div className='mb-2' style={{ fontSize: defaultFontSize }}>
                                <div className='mb-2'>
                                    <span className='title pb-0 mb-1 px-1'>Vital Signs</span>
                                    <div className='d-flex flex-wrap px-2'>
                                        {info?.vitals?.height > 0 && <p className='mr-2 mb-1'><span
                                            className=''><FontAwesomeIcon
                                                icon={faDotCircle}
                                                width={iconSize}
                                                className="text-secondary"
                                            /> H: </span>{info?.vitals?.height + "ft" || " "}
                                        </p>}
                                        {info?.vitals?.weight > 0 && <p className='mr-2 mb-1'><span
                                            className=''><FontAwesomeIcon
                                                icon={faDotCircle}
                                                width={iconSize}
                                                className="text-secondary"
                                            /> W: </span>{info?.vitals?.weight + "kg" || " "}
                                        </p>}
                                        {info?.vitals?.bmi > 0 && <p className='mb-1 mr-2'><span
                                            className=''><FontAwesomeIcon
                                                icon={faDotCircle}
                                                width={iconSize}
                                                className="text-secondary"
                                            /> BMI: </span>{info?.vitals?.bmi || " "}</p>}
                                        {info?.vitals?.bp?.length > 1 && <p className='mb-1 mr-2'><span
                                            className=''><FontAwesomeIcon
                                                icon={faDotCircle}
                                                width={iconSize}
                                                className="text-secondary"
                                            /> BP: </span>{info?.vitals?.bp || " "} mmHg</p>}
                                        {info?.vitals?.temperature && <p className='mb-1 mr-2'><span
                                            className=''><FontAwesomeIcon
                                                icon={faDotCircle}
                                                width={iconSize}
                                                className="text-secondary"
                                            /> Temp: </span>{info?.vitals?.temperature + "°F" || " "}
                                        </p>}
                                        {info?.vitals?.rr && <p className='mb-1 mr-2'><span
                                            className=''><FontAwesomeIcon
                                                icon={faDotCircle}
                                                width={iconSize}
                                                className="text-secondary"
                                            /> RR: </span>{info?.vitals?.rr + "/min" || " "}
                                        </p>}
                                        {info?.vitals?.spo2 && <p className='mb-1 mr-2'><span
                                            className=''><FontAwesomeIcon
                                                icon={faDotCircle}
                                                width={iconSize}
                                                className="text-secondary"
                                            /> SPO2: </span>{info?.vitals?.spo2 + "%" || " "}
                                        </p>}
                                        {info?.vitals?.hr && <p className='mb-1 mr-2'><span
                                            className=''><FontAwesomeIcon
                                                icon={faDotCircle}
                                                width={iconSize}
                                                className="text-secondary"
                                            /> HR: </span>{info?.vitals?.hr + "/min" || " "}
                                        </p>}
                                        {info?.vitals?.note && <p className='mb-1'><span
                                            className=''><FontAwesomeIcon
                                                icon={faDotCircle}
                                                width={iconSize}
                                                className="text-secondary"
                                            /> Note: </span>{info?.vitals?.note}
                                        </p>}

                                    </div>
                                </div>

                            </div>
                        }
                    </div>

                    
                    <div style={{display:medicineAndAdviceOnly?"none":''}}>
                        {
                            drDetails?.config?.pastMedicalHistoryInPrescription && <div className='mb-2' style={{ fontSize: defaultFontSize }}>
                                {
                                    (patientDetails?.pastMedicalHistories?.length > 0) &&
                                    <span className="title my-0 mb-1 px-1">Past Medical History</span>
                                }
                                {patientDetails?.pastMedicalHistories?.length > 0 && patientDetails?.pastMedicalHistories?.map((data, index) => {
                                    return <p className='mb-0 px-2' key={index}>
                                        <FontAwesomeIcon
                                            icon={faDotCircle}
                                            width={iconSize}
                                            className="text-secondary"
                                        />  {data?.name} {data.note ? ": " : ""}{data.note}
                                    </p>
                                })}
                            </div>
                        }
                    </div>
                    <div style={{display:medicineAndAdviceOnly?"none":''}}>
                        {
                            drDetails?.config?.familyHistoryInPrescription && <div className='mb-2' style={{ fontSize: defaultFontSize }}>
                                {
                                    (patientDetails?.familyHistories?.length > 0) &&
                                    <span className="title my-0 mb-1 px-1">Family History</span>
                                }
                                {patientDetails?.familyHistories?.length > 0 && patientDetails?.familyHistories?.map((data, index) => {
                                    return <p className='mb-0 px-2' key={index}><FontAwesomeIcon
                                        icon={faDotCircle}
                                        width={iconSize}
                                        className="text-secondary"
                                    /> {data?.name} : {data?.from}
                                    </p>
                                })}
                            </div>
                        }
                    </div>

                    <div style={{display:medicineAndAdviceOnly?"none":''}}>
                        {
                            drDetails?.config?.surgicalHistoryInPrescription && <div className='mb-1' style={{ fontSize: defaultFontSize }}>
                                {
                                    (patientDetails?.surgicalHistories?.length > 0) &&
                                    <span className="title my-0 mb-1 px-1">Surgical history</span>
                                }
                                {patientDetails?.surgicalHistories?.length > 0 && patientDetails?.surgicalHistories?.map((data, index) => {
                                    return <p className='mb-0 px-2' key={index}><FontAwesomeIcon
                                        icon={faDotCircle}
                                        width={iconSize}
                                        className="text-secondary"
                                    /> {data?.name}
                                    </p>
                                })}
                            </div>
                        }
                    </div>

                    <div style={{display:medicineAndAdviceOnly?"none":''}}>
                        {
                            drDetails?.config?.allergyHistoryInPrescription && <div className='mb-2' style={{ fontSize: defaultFontSize }}>
                                {
                                    (patientDetails?.allergyHistories?.length > 0) &&
                                    <span className="title my-0 mb-1 px-1">Allergy History</span>
                                }
                                {patientDetails?.allergyHistories?.length > 0 && patientDetails?.allergyHistories?.map((data, index) => {
                                    return <p className='mb-0 px-2' key={index}><FontAwesomeIcon
                                        icon={faDotCircle}
                                        width={iconSize}
                                        className="text-secondary"
                                    /> {data?.item} {(data?.severity ? "(" : "") + data?.severity + (data?.severity ? ")" : "")}{data?.reaction ? ": " : ""}{data?.reaction}
                                    </p>
                                })}
                            </div>
                        }
                    </div>

                    <div style={{display:medicineAndAdviceOnly?"none":''}}>
                        {
                            drDetails?.config?.socilHistoryInPrescription && <div className='mb-2' style={{ fontSize: defaultFontSize }}>
                                {
                                    (patientDetails?.socialHistories?.length > 0) &&
                                    <span className="title my-0 mb-1 px-1">Social History</span>
                                }
                                {patientDetails?.socialHistories?.length > 0 && patientDetails?.socialHistories?.map((data, index) => {
                                    return <p className='mb-0 px-2' key={index}><FontAwesomeIcon
                                        icon={faDotCircle}
                                        width={iconSize}
                                        className="text-secondary"
                                    /> {data?.name}{data?.note ? ": " : ""} {data?.note}
                                    </p>
                                })}
                            </div>
                        }
                    </div>


                    {drDetails?.config?.isPhysicalExamShowInPrescription && <div className='mb-2' style={{display:medicineAndAdviceOnly?"none":'', fontSize: defaultFontSize }}>
                        {info?.physicalExams?.length > 0 && <p className='title pb-0 mb-1 px-1'>O/E</p>}
                        <div>
                            {info?.physicalExams?.length > 0 && info?.physicalExams?.map((exam, index) => {
                                // if (((exam?.type?.toLowerCase() == "notexamed" && drDetails?.config?.hideOnExamName) || exam?.type?.toLowerCase() == 'abnormal' || exam?.type?.toLowerCase() == 'normal') && exam?.desc?.length > 1) {
                                if (((exam?.type?.toLowerCase() == "notexamed" && drDetails?.config?.hideOnExamName) || exam?.type?.toLowerCase() == 'abnormal' || exam?.type?.toLowerCase() == 'normal')) {
                                    let data = exam?.desc?.split('\n') || []
                                    return (<div key={index} style={{ padding: '4px', margin: '0px' }}>
                                        {exam?.displayName && <p className='py-0 my-0'>&#8226;{exam?.displayName} {drDetails?.config?.hideOnExamName ? "" : `: ${exam?.type}`} </p>}
                                        {data?.map((d, i) => {
                                            if (d?.length > 1)
                                                return (
                                                    <p key={i} className='py-0 my-0 ml-1'><FontAwesomeIcon
                                                        icon={faDotCircle}
                                                        width={iconSize}
                                                        className="text-secondary"
                                                    /> {d}</p>
                                                )
                                        })}
                                    </div>)
                                }
                            })}
                        </div>
                    </div>}

                    <div style={{display:medicineAndAdviceOnly?"none":''}}>
                        {
                            drDetails?.config?.pastMedicationInPrescription && <div className='mb-2' style={{ fontSize: defaultFontSize }}>
                                {
                                    (patientDetails?.pastMedications?.length > 0) &&
                                    <span className="title my-0 mb-1 px-1">Past Medication</span>
                                }
                                {patientDetails?.pastMedications?.length > 0 && patientDetails?.pastMedications?.map((data, index) => {
                                    return <p className='mb-0 px-2' key={index}>
                                        <FontAwesomeIcon
                                            icon={faDotCircle}
                                            width={iconSize}
                                            className="text-secondary"
                                        />  {medicationLineText(data)}
                                    </p>
                                })}
                            </div>
                        }
                    </div>

                    <div style={{display:medicineAndAdviceOnly?"none":''}}>
                        {
                            <div className='mb-2' style={{ fontSize: defaultFontSize }}>
                                {
                                    provisionalDx?.length > 0 && provisionalDx[0]?.length > 0 && <div className="mb-2">
                                        <p className='title pb-0 mb-0 px-1'>{provisionalDxTitle(drDetails)} </p>
                                        {
                                            provisionalDx?.map((reason, index) => {
                                                if (reason != "")
                                                    return (<p className='my-0 px-2' key={index}><FontAwesomeIcon
                                                        icon={faDotCircle}
                                                        width={iconSize}
                                                        className="text-secondary"
                                                    /> {reason}</p>)
                                            })
                                        }
                                    </div>
                                }
                                {
                                    differentialDx?.length > 0 && differentialDx[0]?.length > 0 &&
                                    <div className='mb-2'>
                                        <p className='title pb-0 mb-0 px-1'>Differential Diagnosis</p>
                                        {
                                            differentialDx?.map((history, index) => {
                                                if (history != "")
                                                    return (<p className='my-0 px-2' key={index}><FontAwesomeIcon
                                                        icon={faDotCircle}
                                                        width={iconSize}
                                                        className="text-secondary"
                                                    /> {history}</p>)
                                            })
                                        }
                                    </div>
                                }
                            </div>
                        }
                    </div>

                    <div className='care-plan' style={{display:medicineAndAdviceOnly?"none":'' }} >
                        {
                            info?.carePlan?.details && info?.carePlan?.details?.length > 0 && info?.carePlan?.details !== "<p><br></p>" && info?.carePlan?.details !== 'undefine' && info?.carePlan?.details != "<p>undefine</p>" && <div className='mb-1 inner-html' style={{ fontSize: defaultFontSize }}>
                                {
                                    (info?.carePlan?.details) &&
                                    <span className="title my-0 mb-1 px-1" style={{ margin: "0px !important" }}>Care Plan</span>
                                }
                                {
                                    info?.carePlan?.details && <div dangerouslySetInnerHTML={{ __html: info?.carePlan?.details }} className="px-2 mb-0">
                                    </div>
                                }
                            </div>
                        }
                    </div>



                    {
                        drDetails?.config?.visionInPrescription &&
                        <div className='' style={{ fontSize: defaultFontSize, display: medicineAndAdviceOnly ? "none" : "" }}>
                            <span className='title pb-0 mb-2'>Visi</span>
                            <div className="spectacle p-1 vision ">
                                {(info?.eyeExamination?.aided?.left || info?.eyeExamination?.aided?.right) &&
                                    <div className='pb-0 mb-0'>
                                        <p className='py-0 my-0'>Visual Acuity: Aided</p>
                                        <p>Right Eye: {info?.eyeExamination?.aided?.left || ""} Left
                                            Eye: {info?.eyeExamination?.aided?.right || ""}</p>
                                    </div>}
                                {(info?.eyeExamination?.unaided?.left || info?.eyeExamination?.unaided?.right) &&
                                    <div className='pb-0 mb-0'>
                                        <p className='py-0 my-0'>Visual Acuity: Unaided</p>
                                        <p>Right Eye: {info?.eyeExamination?.unaided?.left} Left
                                            Eye: {info?.eyeExamination?.unaided?.right}</p>
                                    </div>}
                                {(info?.eyeExamination?.pinHole?.left || info?.eyeExamination?.pinHole?.right) &&
                                    <div className='pb-0 mb-0'>
                                        <p className='py-0 my-0'>Visual Acuity: Pin Hole</p>
                                        <p>Right Eye: {info?.eyeExamination?.pinHole?.left || ""} Left
                                            Eye: {info?.eyeExamination?.pinHole?.right || ""}</p>
                                    </div>}

                                {
                                    info?.eyeExamination?.diagnosis && Object.keys(info?.eyeExamination?.diagnosis).map((exam, index) => (
                                        <div key={index} className='d-flex  my-0'>
                                            <p className='py-0 my-0 pr-1'>{exam?.toUpperCase()}:</p>

                                            {Object.keys(info?.eyeExamination?.diagnosis[exam]).map((side, sideIndex) => (
                                                <p key={sideIndex} className='pr-2  my-0'>
                                                    {side == "left" ? "OS" : "OD"}: {info?.eyeExamination?.diagnosis[exam][side]}
                                                </p>
                                            ))}

                                        </div>
                                    ))
                                }

                                <p className='p-1 m-0 title text-center mt-1' style={{ border: "1px solid dimgrey" }}>Spectacle (Distance)</p>
                                <table
                                    id="example2"
                                    className="table table-hover table-sm table-responsive{-sm|-md|-lg|-xl}"
                                >
                                    <thead>
                                        <tr>
                                            <th>Eye</th>
                                            <th> Sphere</th>
                                            <th>Cylinder</th>
                                            <th>Axis</th>
                                            <th>Acuity</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td className="">Right</td>
                                            <td className="">{info?.eyeExamination ? info?.eyeExamination?.distance?.right?.sphere : ""}</td>
                                            <td className="">{info?.eyeExamination ? info?.eyeExamination?.distance?.right?.cylinder : ""}</td>
                                            <td className="">{info?.eyeExamination ? info?.eyeExamination?.distance?.right?.axis : ""}</td>
                                            <td className="">{info?.eyeExamination ? info?.eyeExamination?.distance?.right?.acuity : ""}</td>
                                        </tr>
                                        <tr>
                                            <td className="">Left</td>
                                            <td className="">{info?.eyeExamination ? info?.eyeExamination?.distance?.left?.sphere : ""}</td>
                                            <td className="">{info?.eyeExamination ? info?.eyeExamination?.distance?.left?.cylinder : ""}</td>
                                            <td className="">{info?.eyeExamination ? info?.eyeExamination?.distance?.left?.axis : ""}</td>
                                            <td className="">{info?.eyeExamination ? info?.eyeExamination?.distance?.left?.acuity : ""}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <div className="spectacle vision m-1">
                                <p className='p-1 m-0 title text-center' style={{ border: "1px solid dimgrey" }}>Near</p>
                                <table
                                    id="example2"
                                    className="table table-hover table-sm table-responsive{-sm|-md|-lg|-xl}"
                                >
                                    <thead>
                                        <tr>
                                            <th>Eye</th>
                                            <th> Addition</th>
                                            <th>Acuity</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td className="">Right</td>
                                            <td className="">{info?.eyeExamination ? info?.eyeExamination?.near?.right?.addition : ""}</td>
                                            <td className="">{info?.eyeExamination ? info?.eyeExamination?.near?.right?.acuity : ""}</td>
                                        </tr>
                                        <tr>
                                            <td className="">Left</td>
                                            <td className="">{info?.eyeExamination ? info?.eyeExamination?.near?.left?.addition : ""}</td>
                                            <td className="">{info?.eyeExamination ? info?.eyeExamination?.near?.left?.acuity : ""}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <div>
                                <p className='py-0 my-0'>IPD: {info?.eyeExamination ? info?.eyeExamination?.ipd : ""}</p>
                                <p>Glasses: {info?.eyeExamination ? info?.eyeExamination?.glasses : ""}</p>
                            </div>
                        </div>
                    }

                    <div style={{display:medicineAndAdviceOnly?"none":''}}>
                        {info?.investigationReports?.length > 0 &&
                            <p className={`title px-1 ${drDetails?.details?.visionInPrescription === "1" ? "pt-1" : "pt-1"}  mb-1`}> Investigation Reports</p>}
                        {info?.investigationReports?.map((labTest, index) => {
                            if (labTest?.findings?.length)
                                return (
                                    <div className='option py-0 my-0'>
                                        <p className='py-0 my-0 px-2'><FontAwesomeIcon
                                            icon={faDotCircle}
                                            width={iconSize}
                                            className="text-secondary"
                                        /> {labTest?.name}:<span style={{ fontStyle: "italic", fontSize: "14px" }} className='py-0 my-0'> {labTest?.findings}</span></p>
                                    </div>
                                )
                        })}
                    </div>

                    {/* Rheumatology disease-activity scores saved on this visit. */}
                    <div style={{display:medicineAndAdviceOnly?"none":''}}>
                        {drDetails?.config?.diseaseActivityInPrescription && info?.diseaseActivityScores?.length > 0 && <>
                            <p className='title px-1 pt-1 mb-1'> Disease Activity</p>
                            {info?.diseaseActivityScores?.map((score, index) => {
                                return (
                                    <p key={"diseaseActivity" + index} className='option my-0 pb-0 px-2'>
                                        <FontAwesomeIcon
                                            icon={faDotCircle}
                                            width={iconSize}
                                            className="text-secondary"
                                        /> {score?.name}{score?.variant ? ` (${score?.variant})` : ""}: <span style={{ fontWeight: "700" }}>{score?.score}</span>{score?.category ? ` — ${score?.category}` : ""}
                                    </p>
                                )
                            })}
                        </>}
                    </div>

                    <div style={{display:medicineAndAdviceOnly?"none":''}}>
                        {info?.labTests?.length > 0 &&
                            <p className={`title px-1 ${drDetails?.details?.visionInPrescription === "1" ? "pt-1" : "pt-1"}  mb-1`}> Investigation</p>}
                        {info?.labTests?.map((labTest, i) => {
                            let labTestArray = labTest?.name?.trim().split('\n') || []
                            return (
                                labTestArray?.map((data, index) => {
                                    return (
                                        <p key={"labTest" + index} className='option my-0 pb-0 px-2'>
                                            <span className="text-secondary py-0 my-0  " style={{ fontWeight: "800", paddingLeft: index == 0 ? "0px" : "14px" }}>{index == 0 ? (i + 1) + ". " : " "}</span>{data}
                                        </p>
                                    )
                                })

                            )
                        })}
                    </div>

                    <div className='mt-1 px-1' style={{display:medicineAndAdviceOnly?"none":''}}>
                        {info?.labTests?.length > 0 && drDetails?.config?.showDiscountNote && discountNote?.length > 0 && discountNote?.map((data, index) => {
                            return (
                                <p style={{ fontWeight: "700" }} key={index} className='option my-0 pb-0'>
                                    {data}
                                </p>
                            )
                        })}
                    </div>

                </div>
                <div style={{
                    zIndex: "3333",
                    width: (100 - (+drDetails?.config?.prescriptionBodyLeftSectionWidth || 34)) + "%"
                }} className={` m-1 ${drDetails?.config?.prescriptionDivider ? "" : "pl-3"}`}>
                    {medicineDesign == 1 && <div>
                        {/* <p className='title pb-0 mb-0 pl-3 pt-2'
                            style={{ fontSize: "18pt" }}>{drDetails?.details?.config?.prescriptionRxTile ? "Rx" : ""}</p> */}
                        {
                            (info?.referredBy) && <div className='d-flex justify-content-end my-0'>

                                <span style={{ fontSize: "14pt" }} className="title my-0">Referred By: {info?.referredBy}</span>
                                {/* <p className='my-0'><span style={{ fontSize: "16px" }}>&#8226;</span> {info?.referredBy}</p> */}
                            </div>

                        }
                        {info?.medicines?.length > 0 && drDetails?.config?.prescriptionRxTile ? <div className='pt-2 ml-1'>
                            <img className='' style={{ width: "45px" }} src={rxImage} alt="Rx" />
                        </div> : <div style={{ height: "65px" }}></div>}
                        {info?.medicines?.map((medicine, index) => {
                            let sigArray = medicine?.sig?.split("\n")
                            return (
                                <div key={index} className='option row no-gutters  p-0 m-0 ml-2' style={{ fontSize: defaultFontSize }}>
                                    <div className='d-flex p-0 m-0 col-5  pl-2'>
                                        {/* <FontAwesomeIcon size='sm' className='mt-3 p-0 m-0 mb-2' icon={faPills} /> */}
                                        <p style={{ fontWeight: "800" }} className='mt-3  p-0 m-0 mb-2  pl-1 text-secondary'>{index + 1}. </p>
                                        <div>
                                            <small className='ml-2 p-0 m-0 mb-2 mt-1 ' style={{
                                                color: "black",
                                                fontStyle: "italic"
                                            }}>{medicine?.type}</small>
                                            <div className='d-flex'>
                                                <span className='ml-2 p-0 m-0 mb-2'
                                                    style={{ fontSize: medicineFontSize, fontWeight: "700" }}>{medicine?.name} </span>
                                                {/* <small className='ml-2 p-0 m-0 mb-2 mt-1' style={{
                                                    color: "black",
                                                    fontStyle: "italic"
                                                }}>{medicine?.power?.slice(0, 10)} </small> */}
                                            </div>
                                        </div>
                                    </div>
                                    {
                                        <div className='mt-3 col-5'>
                                            {
                                                sigArray.length > 0 && sigArray?.map((sig, index) => {
                                                    return (
                                                        <p key={index} className='p-0 m-0 '>{boldParenthesis(sig)}</p>
                                                    )
                                                })
                                            }
                                        </div>
                                    }
                                    <p className='p-0 m-0 mt-3 col-2'>{medicine?.duration?.replace('/', ' ')}</p>
                                </div>
                            )
                        })}
                    </div>}

                    {medicineDesign == 2 && <div>
                        {/* <p className='title pb-0 mb-0 pl-3 pt-2'
                            style={{ fontSize: "18pt" }}>{drDetails?.details?.config?.prescriptionRxTile ? "Rx" : ""}</p> */}
                        {
                            (info?.referredBy) && <div className='d-flex justify-content-end my-0'>

                                <span style={{ fontSize: "14pt" }} className="title my-0">Referred By: {info?.referredBy}</span>
                                {/* <p className='my-0'><span style={{ fontSize: "16px" }}>&#8226;</span> {info?.referredBy}</p> */}
                            </div>

                        }
                        {info?.medicines?.length > 0 && drDetails?.config?.prescriptionRxTile ? <div className='pt-2 ml-2'>
                            <img className='' style={{ width: "45px" }} src={rxImage} alt="Rx" />
                        </div> : <div style={{ height: "65px" }}></div>}
                        {info?.medicines?.map((medicine, index) => {
                            let sigArray = medicine?.sig?.split("\n")
                            return (
                                <div key={index} className='option row no-gutters ml-2' style={{ fontSize: defaultFontSize }}>
                                    <div className='d-flex p-0 m-0 col-12  pl-2 py-1'>
                                        {/* <FontAwesomeIcon size='sm' className='mt-3 p-0 m-0 mb-2' icon={faPills} /> */}
                                        <p style={{ fontWeight: "800" }} className=' p-0 m-0 text-secondary pl-1'>{index + 1}. </p>
                                        <div>
                                            <div className='d-flex my-0 py-0'>
                                                <small className='ml-2 p-0 m-0  mt-1 ' style={{
                                                    color: "black",
                                                    fontStyle: "italic"
                                                }}>{medicine?.type}</small>
                                                <span className='ml-2 p-0 m-0 '
                                                    style={{ fontSize: medicineFontSize, fontWeight: "700" }}>{medicine?.name} </span>
                                                <small className='ml-2 p-0 m-0 mb-0 mt-1' style={{
                                                    color: "black",
                                                    fontStyle: "italic"
                                                }}>{medicine?.generic > 40 && '( '} {medicine?.generic?.length > 40 ? medicine?.generic?.slice(0, 40) + "..." : medicine?.generic} {medicine?.generic > 40 && ' )'} </small>
                                            </div>
                                            <div className='d-flex col-12 my-0 py-0'>
                                                <div className=' py-0 my-0'>
                                                    {
                                                        sigArray.length > 0 && sigArray?.map((sig, index) => {
                                                            return (
                                                                <p key={index} className='p-0 m-0 '>{boldParenthesis(sig)}</p>
                                                            )
                                                        })
                                                    }
                                                </div>
                                                {medicine?.duration && <p className='pl-1 py-0 my-0'> - {medicine?.duration?.replace('/', ' ')}</p>}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )
                        })}
                    </div>}
                    {/* <div className='px-2' style={{ height: "20%", fontSize: defaultFontSize }}> */}
                    <div className='px-2 ml-2 mt-3' style={{ fontSize: defaultFontSize }}>
                        {info?.advices?.length > 0 && <p className='title pb-0 mb-1'>Advice</p>}
                        {info?.advices?.map((advice, index) => {
                            let adviceArray = advice?.details?.trim().split('\n') || []
                            return (
                                adviceArray?.map((data, index) => {
                                    return (
                                        <p key={"advice" + index} className='option my-0 pb-1 ml-2' style={{ fontSize: prescriptionAdviceFontSize || "13pt" }}>
                                            <FontAwesomeIcon
                                                icon={faDotCircle}
                                                width={iconSize}
                                                className="text-secondary"
                                            /> {data}
                                        </p>
                                    )
                                })
                            )

                        })}
                    </div>

                    {referral?.length > 0 && <div className='my-0 py-0 mt-3 px-2 ml-2'>
                        <p style={{ fontSize: defaultFontSize }} className='title my-0 py-0' >Referred To </p>
                        {referral?.map((data, index) => {
                            return (
                                <p key={index} style={{ fontSize: defaultFontSize, fontWeight: "500" }}
                                    className='px-2 my-0 py-0 pb-0 '>{data}</p>
                            )
                        })}
                    </div>}

                    {(info?.followUpDate || (nextVisitInstructions?.length > 0 && nextVisitInstructions[0]?.length > 0)) && <div className='my-0 py-0 mt-1 ml-2'>
                        <p style={{ fontSize: defaultFontSize }} className='title my-0 py-0 px-2' >Next Visit </p>
                        <p style={{ fontSize: defaultFontSize, fontWeight: "500" }}
                            className='px-2 my-0 mt-0 py-0 '>{info?.followUpDate ? "After " + calculateDaysBetween(info?.followUpDate?.slice(0, 10)) + " day(s)" : ""}  </p>
                        {
                            nextVisitInstructions?.length > 0 && nextVisitInstructions[0]?.length > 0 && <div className="mb-2">
                                {
                                    nextVisitInstructions?.map((reason, index) => {
                                        return (<p className='my-0 ml-2 px-2' key={index}> <FontAwesomeIcon
                                            icon={faDotCircle}
                                            width={iconSize}
                                            className="text-secondary"
                                        /> {reason}</p>)
                                    })
                                }
                            </div>
                        }
                        <p style={{ fontSize: defaultFontSize, fontWeight: "500" }}
                            className='px-2 my-0  py-0 '>{info?.followUpDate ? "Date: " + formatDate(info?.followUpDate) : ""} </p>
                    </div>}

                    {
                        (signatureNote?.length > 0 && signatureNote[0]?.length > 0 && (drDetails?.config?.signatureWithImage || drDetails?.config?.signatureWithoutImage || publicPrescriptionSetting?.sig)) &&
                        <div className='pt-3 mt-4 justify-content-end d-flex' style={{ width: "100%", display: medicineAndAdviceOnly ? "none" : "" }}>
                            <div className='d-inline-block'>
                                {(drDetails?.config?.signatureWithImage || publicPrescriptionSetting?.sig) &&
                                    <div className='text-center'>
                                        <img className='' style={{ width: "150px", maxHeight: "150" }} src={signUrl} alt='Sign'></img></div>}
                                <div className='text-center' style={{ borderTop: "1px solid black" }}>
                                    {signatureNote?.length > 0 && signatureNote?.map((data, index) => {
                                        return (
                                            <p key={index} className='option my-0 pb-0' style={{ fontWeight: index == 0 ? "600" : "500", fontSize: index == 0 ? "14px" : "13px" }}>
                                                {data}
                                            </p>
                                        )
                                    })}
                                </div>
                            </div>
                        </div>
                    }
                    {
                        signatureNote?.length == 0 && (drDetails?.config?.signatureWithImage || drDetails?.config?.signatureWithoutImage || publicPrescriptionSetting?.sig) && <div className='pt-3 mt-3' style={{
                            position: "absolute",
                            // top: drDetails?.config?.signatureWithoutImage ? "98%" : "95%",
                            left: "82%",
                            display: medicineAndAdviceOnly ? "none" : ""    
                        }}>
                            {(drDetails?.config?.signatureWithImage || publicPrescriptionSetting?.sig) &&
                                <img style={{ width: "150px", maxHeight: "150" }} src={signUrl} alt=''></img>}
                            <p style={{ borderTop: "1px solid black" }} className='px-5'>Signature</p>
                        </div>
                    }

                    {/* {info?.followUpReason && <div className='my-0 py-0'>
                        <p style={{ fontSize: defaultFontSize }} className='my-0 py-0 px-2'>Next Visit Reason</p>
                        <p style={{ fontSize: defaultFontSize, fontWeight: "600" }}
                            className='px-2'>{info?.followUpReason}</p>
                    </div>} */}
                </div>
            </div>
            {drDetails?.config?.prescriptionBackgroundImage &&
                <div className='d-flex justify-content-center align-content-center' style={{
                    zIndex: "1",
                    position: "absolute",
                    height: "400px",
                    width: "600px",
                    top: "30%",
                    left: "21%"
                }}>
                    <div className='my-auto' style={{ maxWidth: "400" }}>
                        <img style={{ opacity: ".2" }} src={backGround}></img>
                    </div>
                </div>}
            {/* {
                (drDetails?.config?.signatureWithImage || drDetails?.config?.signatureWithoutImage) &&
                <div style={{
                    position: "absolute",
                    // top: drDetails?.config?.signatureWithoutImage ? "98%" : "95%",
                    left: "82%"
                }}>
                    {drDetails?.config?.signatureWithImage &&
                        <img style={{ width: "200px", maxHeight: "200" }} src={signUrl} alt='check'></img>}
                    <p style={{ borderTop: "1px solid black" }} className='px-5'>Signature</p>
                </div>
            } */}
        </div>
    )
}

export default PrescriptionBody
